import React from 'react';
import { Alert, Box, LinearProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { GetVisitationTeamInsightsResponse } from '../../api-service/clients';
import { CircularProgressCentered } from '../../components/circular-progress-centered/circular-progress-centered';

interface VisitationTeamInsightsComponentProps {
  insights: GetVisitationTeamInsightsResponse | undefined;
  isLoading: boolean;
}

function averageOccupancy(insights: GetVisitationTeamInsightsResponse): number | undefined {
  const meetingMinutes = insights.occupancy.reduce((sum, meeting) => sum + meeting.meetingMinutes, 0);
  const bookedMinutes = insights.occupancy.reduce((sum, meeting) => sum + meeting.bookedMinutes, 0);
  return meetingMinutes === 0 ? undefined : Math.round((100 * bookedMinutes) / meetingMinutes);
}

export function VisitationTeamInsightsComponent({ insights, isLoading }: Readonly<VisitationTeamInsightsComponentProps>) {
  if (isLoading || !insights) {
    return <CircularProgressCentered />;
  }

  const average = averageOccupancy(insights);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">Ventetid til næste ledige tid</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Dage fra i dag til første ledige tid for en sag med bevillingstypen, efter lukkefristen.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Bevillingstype</TableCell>
              <TableCell align="right">Standardtid</TableCell>
              <TableCell>Næste ledige tid</TableCell>
              <TableCell align="right">Ventetid</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {insights.waitingTimes.map(waitingTime => (
              <TableRow key={waitingTime.grantTypeName}>
                <TableCell>{waitingTime.grantTypeName}</TableCell>
                <TableCell align="right">{waitingTime.standardMinutes} min</TableCell>
                <TableCell>{waitingTime.firstFreeStartAt ? format(new Date(waitingTime.firstFreeStartAt), 'EEE d.M. HH:mm', { locale: da }) : 'Ingen ledig tid'}</TableCell>
                <TableCell align="right">{waitingTime.waitingDays != null ? `${waitingTime.waitingDays} dage` : '–'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">Belægningsgrad pr. møde</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Reserveret tid i forhold til mødets længde{average != null ? ` · gennemsnit ${average} %` : ''}.
        </Typography>
        {insights.occupancy.length === 0 && <Alert severity="info">Teamet har ingen møder i perioden.</Alert>}
        {insights.occupancy.map(meeting => (
          <Box key={String(meeting.startAt)} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
            <Typography variant="body2" sx={{ width: 120 }}>
              {format(new Date(meeting.startAt), 'EEE d.M.', { locale: da })}
            </Typography>
            <LinearProgress variant="determinate" value={Math.min(meeting.occupancyPercent, 100)} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
            <Typography variant="body2" sx={{ width: 48, textAlign: 'right' }}>
              {meeting.occupancyPercent} %
            </Typography>
          </Box>
        ))}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, gridColumn: { lg: '1 / span 2' } }}>
        <Typography variant="h6">Tilkendt og afvist pr. bevillingstype</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Teamets beslutninger på behandlede sager i perioden på tværs af sagsbehandlere.
        </Typography>
        {insights.decisions.length === 0 ? (
          <Alert severity="info">Teamet har ingen behandlede sager i perioden.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bevillingstype</TableCell>
                <TableCell align="right">Tilkendt</TableCell>
                <TableCell align="right">Afvist</TableCell>
                <TableCell align="right">Andel tilkendt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {insights.decisions.map(decision => (
                <TableRow key={decision.grantTypeName}>
                  <TableCell>{decision.grantTypeName}</TableCell>
                  <TableCell align="right">{decision.granted}</TableCell>
                  <TableCell align="right">{decision.rejected}</TableCell>
                  <TableCell align="right">{Math.round((100 * decision.granted) / Math.max(1, decision.granted + decision.rejected))} %</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
